// Layout, zoom and track constants for the dubbing Timeline (AIV-12773).
import { VOLUME_TRACK } from './project';

export const RULER_HEIGHT = 24;
export const TRACK_HEIGHT = 48;
export const CLIP_HEIGHT = 40;
export const ROW_GAP = 8;

// Media track is taller: frames (TRACK_HEIGHT) over a waveform band. Its gutter
// cell matches so rows stay aligned.
export const WAVEFORM_HEIGHT = 24;
export const MEDIA_TRACK_HEIGHT = TRACK_HEIGHT + WAVEFORM_HEIGHT;

// Left inset of the timeline coordinate origin so the 0s tick / first clip are
// not clipped at the viewport edge. Baked into secToPx/pxToSec.
export const START_OFFSET = 8;

// Trailing space (px) after the last track content so the end (e.g. the media
// track, which has no append button) isn't flush against the viewport edge. Fit
// reserves it so it never causes a horizontal scroll.
export const CONTENT_END_PAD_PX = 36;

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 20;
export const DEFAULT_ZOOM = 5;
export const TIMELINE_PANEL_HEIGHT = 57;

export const ZOOM_STEP = 1;

// Wide label gap so majors land every 5s at the fitted zoom (matches design),
// each major split into 4 minor dots. Zooming in naturally shifts to 1s/2s.
export const MIN_LABEL_GAP_PX = 120;
export const MINOR_PER_MAJOR = 5;

export const APPEND_GAP_PX = 8;

// Breathing room (px) kept on either side when scrolling a clip into view, so a
// revealed clip never sits flush against the viewport edge.
export const CLIP_REVEAL_MARGIN_PX = 48;

// Width (px) of the "a block is still coming" placeholder shown on the voice
// lane while transcript extraction streams blocks in. Fixed, not derived from
// time: the block's duration isn't known until it arrives, so a time-based
// width would rescale with zoom and jump on every batch.
export const CLIP_SKELETON_WIDTH_PX = 220;

// Drag-seek auto-scroll: while scrubbing the playhead, once the pointer enters
// this px-wide band at either viewport edge the timeline scrolls that way, up
// to EDGE_SCROLL_MAX_SPEED px per animation frame (ramped by how deep into the
// band the pointer is), so the user can seek past the visible window.
export const EDGE_SCROLL_SIZE_PX = 48;
export const EDGE_SCROLL_MAX_SPEED_PX = 24;

export const BLOCK_VARIANT = {
  ERROR: 'error',
  TRIMMED: 'trimmed',
  SELECTED: 'selected',
  DEFAULT: 'default',
};

// Reserved height (px) for the per-block audio waveform inside a trimmed clip.
// The waveform itself is rendered later; for now the clip leaves this space so
// its layout already matches the design.
export const CLIP_WAVEFORM_HEIGHT = 12;

// Stacking order inside the scrolling timeline content. The playhead line sits
// above the lanes so it reads across every track. A selected clip's trim handles
// sit above IT: the line's grab strip is 12px wide, centred on the line, and
// focusing a block parks the playhead exactly on that block's start — right on
// top of its left trim handle. Below the line, the strip would swallow every
// press aimed at the handle and scrub instead of trimming.
export const PLAYHEAD_LINE_Z = 5;
export const TRIM_HANDLE_Z = 6;

// Minimum clip width (px) to bother decoding + drawing a voice block's audio
// waveform. Below this the envelope is illegible, so we skip the (network +
// decode) work — which also stops a big project from fetching every block's
// audio at the fitted zoom (all clips visible but tiny). The set grows as the
// user zooms in.
//
// 16px, not the 48 this started at: zoom is exponential (0.1 → 240 px/s over 20
// levels), so 48px kept a typical ~3s block dark until zoom ~14 of 20 — three
// steps past where its 8px-inset slot has room to draw. The concurrency bound in
// useBlockWaveforms (3 at a time, cached + deduped per url) is what actually
// caps the cost; the viewport caps the count (~viewportWidth / this).
export const WAVEFORM_MIN_CLIP_PX = 16;

// Drag/crop tuning. minDuration = shortest a block's slot may be trimmed to;
// dragThreshold = px of movement before a press becomes a drag (below it a
// press is a click/select); handleHit = invisible grab width over the thin trim
// bar; snap = px within which a dragged edge snaps to a candidate.
export const CLIP_MIN_DURATION_SEC = 0.2;
export const DRAG_THRESHOLD_PX = 3;
export const TRIM_HANDLE_HIT_PX = 10;
export const SNAP_THRESHOLD_PX = 8;

// A clip's audio counts as "trimmed" only when its playback is cut by more than
// this — guards against flagging an exact fit as trimmed when the measured audio
// duration and the (shrink-to-fit realigned) slot differ by a few milliseconds.
export const TRIM_EPSILON_SEC = 0.001;

// Media-track thumbnail extraction states.
export const THUMB_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  FAILED: 'failed',
};

// The timeline's lanes, top to bottom. Lane keys index both the gutter cells and
// the lane components, so they live here rather than inline in the container.
export const TIMELINE_LANE = {
  MEDIA: 'media',
  VOICE: 'voice',
  MUSIC: 'music',
};

// Lane → the mixer track it controls. The voice lane drives the `voiceover`
// level; the other two match by name.
export const VOLUME_TRACK_BY_LANE = {
  [TIMELINE_LANE.MEDIA]: VOLUME_TRACK.MEDIA,
  [TIMELINE_LANE.VOICE]: VOLUME_TRACK.VOICEOVER,
  [TIMELINE_LANE.MUSIC]: VOLUME_TRACK.MUSIC,
};
